"use client";
import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useUser } from "@/app/context/UserContext";
import { iProProjectMain } from "@/app/models/interfaceProject";

type Metric = {
  key: string;
  name: string;
  description: string;
  requirement: string;
  realtime: boolean;
};

const METRICS: Metric[] = [
  { key: "coherence", name: "Coherencia", description: "Evalúa si la respuesta está bien estructurada, es lógica y fácil de entender.", requirement: "respuesta", realtime: true },
  { key: "completeness", name: "Completitud", description: "Evalúa si la respuesta cubre todos los aspectos de la pregunta del usuario.", requirement: "respuesta sobre pregunta", realtime: true },
  { key: "answerConciseness", name: "Concisión De La Respuesta", description: "Evalúa qué tan concisa y eficiente es la respuesta (métrica Azure).", requirement: "respuesta sobre pregunta", realtime: true },
  { key: "consistency", name: "Consistencia", description: "Evalúa si la respuesta es consistente con los turnos anteriores de la conversación.", requirement: "contexto e historial", realtime: true },
  //{ key: "answerCorrectness", name: "Corrección De La Respuesta", description: "Evalúa la corrección factual de la respuesta respecto a una Verdad Fundamental.", requirement: "groundTruth (si aplica)", realtime: false },
  { key: "faithfulness", name: "Fidelidad", description: "Evalúa la fidelidad de la respuesta al contexto proporcionado.", requirement: "contexto", realtime: true },
  { key: "fluency", name: "Fluidez", description: "Evalúa la calidad gramatical y lingüística de la respuesta.", requirement: "respuesta", realtime: true },
  { key: "groundedness", name: "Fundamentación", description: "Evalúa si la respuesta está basada en el contexto proporcionado.", requirement: "contexto", realtime: true },
  { key: "contextPrecision", name: "Precisión Del Contexto", description: "Evalúa qué tan precisa es la información utilizada del contexto (métrica Azure).", requirement: "contexto", realtime: true },
  { key: "contextRecall", name: "Recuperación Del Contexto", description: "Evalúa qué tan bien la respuesta recupera información del contexto (métrica Azure).", requirement: "contexto", realtime: true },
  { key: "relevance", name: "Relevancia", description: "Evalúa si la respuesta aborda directamente la pregunta del usuario.", requirement: "respuesta sobre pregunta", realtime: true },
  { key: "contextRelevance", name: "Relevancia Del Contexto", description: "Evalúa qué tan relevante es el contexto para responder la pregunta (métrica Azure).", requirement: "contexto", realtime: true },
  //{ key: "safety", name: "Seguridad", description: "Evalúa si la respuesta cumple con las directrices de seguridad y ética.", requirement: "safetyGuidelines (si aplica)", realtime: true },
  //{ key: "bias", name: "Sesgo", description: "Evalúa si la respuesta presenta sesgos o discriminación.", requirement: "", realtime: true },
];

export default function Evaluacion() {
  const { userProject, setUserProject } = useUser();
  const [search, setSearch] = useState("");

  const selectedMetrics = useMemo(
    () => new Set(userProject?.project?.evaluation?.metrics || []),
    [userProject]
  );

  const [enabled, setEnabled] = useState<boolean>(
    userProject?.project?.evaluation?.active ?? selectedMetrics.size > 0
  );
  
  useEffect(() => {
    setEnabled(userProject?.project?.evaluation?.active ?? false);
  }, [userProject?.project?.evaluation?.active]);

  const filteredMetrics = useMemo(() => {
    const query = search.toLowerCase();
    return METRICS.filter(
      (m) =>
        m.name.toLowerCase().includes(query) ||
        m.description.toLowerCase().includes(query) ||
        m.key.toLowerCase().includes(query)
    );
  }, [search]); 

  const toggleEvaluation = () => {
    const newState = !enabled;
    setEnabled(newState);

    setUserProject((prev: iProProjectMain) => ({
      ...prev,
      project: {
        ...prev.project,
        evaluation: {
          ...(prev.project.evaluation || { metrics: [], active: false }),
          active: newState,
          metrics: newState ? prev.project.evaluation?.metrics || [] : [],
        },
      },
    }));
  };
  
  const toggleMetric = (key: string) => {
    const current = new Set(selectedMetrics);
    current.has(key) ? current.delete(key) : current.add(key);

    setUserProject((prev: iProProjectMain) => ({
      ...prev,
      project: {
        ...prev.project,
        evaluation: {
          ...(prev.project.evaluation || { active: true, metrics: [] }),
          active: true,
          metrics: Array.from(current),
        },
      },
    }));
  };

  return (
    <div className="p-6 space-y-5 w-full">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Evaluación</h3>
          <p className="text-sm text-gray-500">
            Activa la evaluación y selecciona las métricas que deseas aplicar.
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={enabled}
            onChange={toggleEvaluation}
            className="w-5 h-5 accent-blue-600 cursor-pointer"
          />
          <span className="text-sm font-medium text-gray-700">
            Activar evaluación
          </span>
        </label>
      </div>

      {/* Buscador */}
      {enabled && (
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar métrica..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-3 pr-8 py-2 border rounded n-fmr-input-search border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700"
          />
          <Search className="absolute right-3 top-3 text-gray-400 w-6 h-6" />
        </div>
      )}

      {/* Lista de métricas */}
      {enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {filteredMetrics.map((m) => {
            const selected = selectedMetrics.has(m.key);
            return (
              <label
                key={m.key}
                className={`p-3 rounded-lg border transition-all cursor-pointer ${
                  selected
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                } flex items-start gap-3`}
              >
                <input
                  type="checkbox"
                  className="mt-1 w-4 h-4 accent-blue-600"
                  checked={selected}
                  onChange={() => toggleMetric(m.key)}
                />
                <div className="text-sm">
                  <div className="font-semibold text-gray-800">{m.name}</div>
                  <div className="text-xs text-gray-600 leading-snug mt-0.5">
                    {m.description}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {m.requirement && <span>Requerido: {m.requirement}</span>}
                    {m.realtime && (
                      <span className="ml-2 px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs font-medium">
                        Tiempo real
                      </span>
                    )}
                  </div>
                </div>
              </label>
            );
          })}
          {filteredMetrics.length === 0 && (
            <p className="text-sm text-gray-400 col-span-3 text-center py-6">
              No se encontraron métricas para “{search}”.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
